// filepath: apps/web/src/admin/people/new.tsx
//
// ISS-518 — PersonNewScreen (POST /api/people).
//
// Mirrors the counterparty + locations pattern (ISS-442 UX slim +
// i18n + permission-denied surfaces). The lighter SPA protocol
// for Slice 1 applies: no react-router-dom, no raw fetch(), no
// synthesised display fields (INV-2).
//
// INV-7: row_version is server-assigned on create; the form
// does NOT carry one.
//
// INV-8: a fresh X-Idempotency-Key per submit (mirrors the
// counterparty pattern — the SSR-side replay guard returns
// 200 on double-submit rather than producing a duplicate row).

import React, { useCallback, useState } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { PermissionDenied } from "@/design-system/error-states/PermissionDenied";
import { ApiError } from "@/definitions/types";
import { navigate } from "@/features/cabinet/usePathname";
import { PersonForm, type PersonFormValues } from "./PersonForm";
import { createPerson, newIdempotencyKey, type CreatePersonInput } from "./api";

function isPermissionDenied(e: unknown): boolean {
  if (e instanceof ApiError) return e.status === 403 || e.status === 404;
  return false;
}

export interface PersonNewScreenProps {
  /** Test override for the submit pipeline. */
  submit?: (input: CreatePersonInput, key: string) => Promise<void>;
}

export function PersonNewScreen({
  submit,
}: PersonNewScreenProps = {}): React.ReactElement {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [permissionDenied, setPermissionDenied] = useState<boolean>(false);

  const onSubmit = useCallback(
    async (values: PersonFormValues) => {
      setSubmitting(true);
      setError(null);
      setPermissionDenied(false);
      try {
        const key = newIdempotencyKey();
        const input = {
          display_name: values.display_name,
          email: values.email === "" ? null : values.email,
          phone: values.phone === "" ? null : values.phone,
          status: values.status,
        };
        if (submit) {
          await submit(input, key);
        } else {
          await createPerson(input, key);
        }
        navigate("/admin/people");
      } catch (e) {
        if (isPermissionDenied(e)) {
          setPermissionDenied(true);
        } else {
          setError(e instanceof Error ? e.message : String(e));
        }
      } finally {
        setSubmitting(false);
      }
    },
    [submit],
  );

  if (permissionDenied) {
    return (
      <div data-testid="person-new-permission-denied">
        <PermissionDenied />
      </div>
    );
  }

  return (
    <PersonForm
      heading={t("directory.people.new.title", "New person")}
      initial={{
        display_name: "",
        email: "",
        phone: "",
        status: "active",
      }}
      onSubmit={onSubmit}
      onCancel={() => navigate("/admin/people")}
      submitting={submitting}
      error={error}
      submitLabel={t("directory.people.new.submit", "Create person")}
      loadingLabel={t("directory.people.new.submitting", "Creating…")}
    />
  );
}

export default PersonNewScreen;
